import { DEFAULT_TCO } from './constants'
import { DHLibConfig, DHTco, DHUserMode } from '../../../types/design-huddle.types'
import { DesignHuddleContext, DesignHuddleState } from './context'
import { RootCodeState } from '@dtx-company/inter-app/src/redux/types'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { buildDHConfigObject } from './utils'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useContext, useMemo } from 'react'
import { useFlowRouter } from '@dtx-company/inter-app/src/hooks/useFlowRouter'

/**
 * Custom hook to access the DesignHuddle context.
 *
 * @note This hook is only available within a {@link DesignHuddleProvider}.
 */
export const useDesignHuddle = (): DesignHuddleState => {
  const context = useContext(DesignHuddleContext)
  if (context === undefined) {
    throw new Error('useDesignHuddle must be used within a DesignHuddleProvider')
  }
  return context
}

/**
 * Returns a memozied DesignHuddle TCO constructed from the current Brand Kit.
 */
export const useBrandKitTco = (): DesignHuddleState['brandKitTco'] => {
  const brandKit = useCodeSelector((state: RootCodeState) => state.printStore.brandKit)

  const router = useFlowRouter()

  const flowcodeImageUrl = useMemo(() => {
    switch (router.pathname) {
      case Routes.PRINT_STORE_PRODUCT_TEMPLATES:
        return brandKit.templatesListFlowcodeImage
      case Routes.PRINT_STORE_EDIT_TEMPLATE:
        return brandKit.editorFlowcodeImage
      case Routes.TEMPLATES_PLACEMENT:
        return brandKit.highlightedTemplatesFlowcodeImage
      default:
        return DEFAULT_TCO.flowcodeImage.url
    }
  }, [
    router.pathname,
    brandKit.editorFlowcodeImage,
    brandKit.templatesListFlowcodeImage,
    brandKit.highlightedTemplatesFlowcodeImage
  ])

  const brandKitTco: DHTco = useMemo(() => {
    if (!brandKit) {
      return DEFAULT_TCO
    }
    return {
      flowcodeImage: {
        url: flowcodeImageUrl || DEFAULT_TCO.flowcodeImage.url
      },
      ...(brandKit.primaryText && {
        primaryText: {
          text: brandKit?.primaryText,
          auto_fit: true
        }
      }),
      ...(brandKit.primaryColor && {
        primaryColor: {
          color: brandKit?.primaryColor
        }
      }),
      ...(brandKit.secondaryColor && {
        secondaryColor: {
          color: brandKit?.secondaryColor
        }
      }),
      ...(brandKit.logoImage && {
        logoImage: {
          url: brandKit?.logoImage
        }
      })
    }
  }, [brandKit, flowcodeImageUrl])

  return brandKitTco
}

/**
 * Returns a memoized DesignHuddle config object constructed from the current Brand Kit.
 */
export const useDHConfig = ({ mode }: { mode: DHUserMode }): DHLibConfig => {
  const tco = useBrandKitTco()
  const dhConfig = useMemo<DHLibConfig>(() => buildDHConfigObject({ mode, tco }), [tco, mode])

  return dhConfig
}
